/**
 * Options for checking render stability
 */
interface RenderStabilityOptions {
  /** Maximum time to wait for stability in milliseconds */
  maxWaitTime?: number;
  /** Number of stable frames required before considering the render complete */
  minStableFrames?: number;
  /** CSS selectors for elements that must be present and visible */
  requiredSelectors?: string[];
}

/**
 * Waits for the page render to stabilize by monitoring visual changes
 * Uses requestAnimationFrame for better performance and cross-browser compatibility
 */
async function waitForStableRender(
  options: RenderStabilityOptions = {}
): Promise<void> {
  const {
    maxWaitTime = 2000,
    minStableFrames = 3,
    requiredSelectors = [],
  } = options;

  return new Promise<void>((resolve) => {
    const startTime = performance.now();
    let stableFrameCount = 0;
    let lastHeight = 0;
    let rafId: number;

    // Check if required elements are present and visible
    const checkRequiredElements = (): boolean => {
      return requiredSelectors.every((selector) => {
        const element = document.querySelector(selector);
        if (!element) return false;
        const rect = element.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      });
    };

    const checkStability = (): void => {
      // Check if we've exceeded maxWaitTime
      if (performance.now() - startTime > maxWaitTime) {
        cancelAnimationFrame(rafId);
        resolve();
        return;
      }

      // Get current document height
      const currentHeight = document.documentElement.offsetHeight;

      // Check if height is stable and required elements are present
      if (
        currentHeight === lastHeight &&
        (!requiredSelectors.length || checkRequiredElements())
      ) {
        stableFrameCount++;
        if (stableFrameCount >= minStableFrames) {
          cancelAnimationFrame(rafId);
          resolve();
          return;
        }
      } else {
        stableFrameCount = 0;
      }

      lastHeight = currentHeight;
      rafId = requestAnimationFrame(checkStability);
    };

    // Start checking once the document is ready
    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        () => {
          rafId = requestAnimationFrame(checkStability);
        },
        { once: true }
      );
    } else {
      rafId = requestAnimationFrame(checkStability);
    }
  });
}

export { waitForStableRender, RenderStabilityOptions };
